import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useAskRedoSlidePanel } from "@redotech/redo-merchant-app-common/ask-redo/ask-redo-slide-panel-context";
import {
  clearEscalationNotification,
  useEscalationContext,
  useEscalationNotifications,
  useEscalationSubscription,
  type HumanMessage,
} from "@redotech/redo-merchant-app-common/escalation";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { memo, useCallback, useEffect, useRef } from "react";
import { chatInstances } from "../hooks/use-ask-redo-chat";
import { useEscalationAuthor } from "./escalation-author-context";
import { humanMessageToUIMessage } from "./intercom-to-uimessage";

/**
 * The Ask Redo escalation **data layer**. Always mounted at the
 * layout level. Owns the single SSE subscription, drives the toast
 * when the panel is closed, captures the human agent's name +
 * avatar into EscalationAuthorContext, and pushes live human
 * replies into the cached Chat instance so they appear whether the
 * slide-out is mounted right now or later.
 *
 * Renders nothing. The slide-out is purely a view onto this state.
 */
export const AskRedoEscalationWatcher = memo(
  function AskRedoEscalationWatcher() {
    const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);
    const user = useRequiredContext(UserContext);
    const escalationContext = useEscalationContext();
    const author = useEscalationAuthor();
    const { isOpen, open } = useAskRedoSlidePanel();

    // Ref-stable triage thread id for the live-message dispatch.
    //
    // We deliberately read from `escalation.triageThreadId` (bound to
    // the active escalation at creation/hydration time) rather than
    // the floating `triageThreadId` setter on context. The floating
    // value is "currently displayed thread" — it gets overwritten
    // every time the merchant clicks a different conversation from
    // history. If the watcher used that for routing, opening an
    // unrelated thread mid-escalation would land the next admin
    // reply on the wrong cached chat.
    const escalationTriageThreadId =
      escalationContext.escalation.triageThreadId;
    const triageThreadIdRef = useRef(escalationTriageThreadId);
    useEffect(() => {
      triageThreadIdRef.current = escalationTriageThreadId;
    }, [escalationTriageThreadId]);

    // Ref-stable panel-open state for the closed-panel toast branch in
    // the SSE handler. We don't re-subscribe on `isOpen` flips — the
    // SSE stream is bound to escalationId.
    const isOpenRef = useRef(isOpen);
    useEffect(() => {
      isOpenRef.current = isOpen;
    }, [isOpen]);

    // Refs the notification hook expects.
    const hasInitializedRef = useRef(false);
    const pendingThreadLoadInFlightRef = useRef(false);

    // When the toast is clicked, open the Ask Redo slide-out with
    // the right thread id. The slide-out's `useAskRedoThreadHistory`
    // hydrates from the panel options.
    const loadThreadIntoChat = useCallback(
      async (threadId: string) => {
        open({ kind: "thread", threadId });
      },
      [open],
    );

    // Register the "navigate to thread" callback on EscalationContext
    // so the escalate-to-human card's "Go to existing" button works.
    // Without this the button is permanently disabled (help-chat used
    // to register this; Ask Redo had no wiring).
    const setNavigateToThread = escalationContext.setNavigateToThread;
    useEffect(() => {
      setNavigateToThread((threadId: string) => {
        open({ kind: "thread", threadId });
      });
      return () => setNavigateToThread(null);
    }, [setNavigateToThread, open]);

    const { notifyClosedEscalationMessage } = useEscalationNotifications({
      isOpen,
      open,
      threads: [],
      escalationContext,
      merchantRpcClient,
      getThreads: merchantRpcClient.getAskRedoThreads,
      loadThreadIntoChat,
      userEmail: user.email,
      hasInitializedRef,
      pendingThreadLoadInFlightRef,
    });

    // The single SSE listener for human replies. Pushes the message
    // into the cached Chat instance keyed by triageThreadId — works
    // whether the slide-out is mounted, unmounted, or about to mount.
    // Also captures the agent's first name + avatar so the banner
    // and message bubble renderers can read them from
    // EscalationAuthorContext.
    const handleHumanMessage = useHandler((message: HumanMessage) => {
      author.recordAuthor({
        name: message.authorName?.split(" ")[0] ?? null,
        avatarUrl: message.authorAvatarUrl ?? null,
      });

      const triageThreadId = triageThreadIdRef.current;
      if (triageThreadId) {
        const chat = chatInstances.get(triageThreadId);
        if (chat) {
          const ui = humanMessageToUIMessage(message);
          // The Chat class only exposes a `messages` getter/setter —
          // the `setMessages` updater lives on the useChat hook's
          // return value, NOT on the Chat instance. Use the setter
          // directly; ReactChatState's setter fires the subscribed
          // useSyncExternalStore callbacks so every mounted useChat
          // re-renders with the appended message.
          const prev = chat.messages;
          if (!prev.some((m) => m.id === ui.id)) {
            chat.messages = [...prev, ui];
          }
        }
      }

      // Closed-panel branch: fire the system notification toast so a
      // support reply that arrives while the merchant is in another
      // surface (orders, returns, etc.) doesn't land silently. The
      // toast click routes through `useEscalationNotifications`'
      // `pendingThreadToOpen` flow, which calls `loadThreadIntoChat`
      // to reopen the panel on the right thread.
      //
      // The full-page Ask Redo route (`/ask-redo` or
      // `/stores/<teamId>/ask-redo/...`) is ALSO an "open" view of
      // the conversation — toasting there would notify the merchant
      // about a reply they're already looking at. Check
      // `window.location.pathname` directly rather than threading a
      // router-aware context through every consumer; the watcher is
      // already a side-effect-y data layer, this is the cheapest
      // detection. The escalation's bound thread also has to match
      // the path's `:threadId` for the suppression to apply, so a
      // toast on an unrelated full-page session still fires.
      if (
        !isOpenRef.current &&
        !isViewingEscalationOnFullPage(escalationTriageThreadId)
      ) {
        notifyClosedEscalationMessage(message);
      }
    });
    useEscalationSubscription(handleHumanMessage);

    // Cold-start backfill: on mount (or when the active escalation
    // changes) the SSE stream only carries FUTURE events. To populate
    // agentName/avatar from history (e.g., after a page refresh
    // mid-conversation), fetch escalation messages once and feed the
    // newest admin author through `recordAuthor`.
    const escalationId = escalationContext.escalation.escalationId;
    useEffect(() => {
      if (!escalationId) {
        return;
      }
      let cancelled = false;
      void (async () => {
        try {
          const result = await merchantRpcClient.getEscalationMessages({
            escalationId,
          });
          if (cancelled) {
            return;
          }
          // Newest admin message wins — that's the one most likely
          // to reflect the current assignee.
          const latestAdmin = [...result.messages]
            .reverse()
            .find((m) => m.authorName || m.authorAvatarUrl);
          if (latestAdmin) {
            author.recordAuthor({
              name: latestAdmin.authorName?.split(" ")[0] ?? null,
              avatarUrl: latestAdmin.authorAvatarUrl ?? null,
            });
          }
        } catch {
          // Backfill is best-effort; SSE will populate on next reply.
        }
      })();
      return () => {
        cancelled = true;
      };
      // `author.recordAuthor` is stable; intentionally not in deps to
      // avoid re-fetching on author updates.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [escalationId, merchantRpcClient]);

    // Clear agent info when no escalation is in context (closed +
    // cleared, or post "New Conversation"). Prevents an old agent's
    // name/avatar from bleeding into the next escalation.
    useEffect(() => {
      if (!escalationId) {
        author.clearAuthor();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [escalationId]);

    // When Ask Redo's panel opens, dismiss any escalation toast that
    // was sitting from a previous closed-panel notification — the
    // merchant is now looking at the chat.
    useEffect(() => {
      if (isOpen) {
        clearEscalationNotification();
      }
    }, [isOpen]);

    return null;
  },
);

/**
 * True when the user is currently on the full-page Ask Redo route AND
 * the path's `:threadId` segment matches the escalation's bound
 * thread. In that case the conversation is already on screen — a
 * notification toast would just duplicate the message bubble that's
 * about to render via the same SSE event.
 *
 * Reads `window.location.pathname` directly rather than threading a
 * `useLocation` consumer down to the watcher: the watcher is a
 * pure side-effect node with no render output, and the SSE handler
 * runs outside the React commit cycle anyway — a stale subscription
 * to react-router state would be the more fragile choice.
 */
function isViewingEscalationOnFullPage(
  boundTriageThreadId: string | null,
): boolean {
  if (typeof window === "undefined" || !boundTriageThreadId) {
    return false;
  }
  // Matches:
  //   /ask-redo
  //   /ask-redo/<threadId>
  //   /stores/<teamId>/ask-redo
  //   /stores/<teamId>/ask-redo/<threadId>
  const match = window.location.pathname.match(
    /\/ask-redo(?:\/([^/?#]+))?(?:[/?#]|$)/,
  );
  if (!match) {
    return false;
  }
  const pathThreadId = match[1];
  // Only suppress when the path's thread id explicitly matches the
  // escalation's bound thread. The bare `/ask-redo` (no segment) is
  // the "new conversation" hero — a toast there IS useful because
  // the merchant isn't looking at the escalated transcript.
  return pathThreadId === boundTriageThreadId;
}
